//
// Copyright (c) 2026 Comcast
//

import { bgSecondaryDefaultExpand } from 'constants/colors'
import { removeUndefinedValues } from 'utils/object'

// convert description to a unified format:
// [{
//   group: group name or null if no groups,
//   fieldParams: [
//     {...},
//     ...
//   ]
// }, ... ]
export function getFieldGroups (fieldParams) {
  const hasGroups = fieldParams.length > 0 && (typeof fieldParams[0].group !== 'undefined')
  const result = hasGroups ? fieldParams : [{ group: null, fieldParams: fieldParams }]

  return result
}

export function hasRequiredFields (fieldGroups) {
  for (const g of fieldGroups) {
    if (g.fieldParams.some(f => f.required)) {
      return true
    }
  }
  return false
}

export function getValidator (value, required, validateFunc) {
  const requiredValidateError = required && requiredValidator(value)
  const customValidateError = validateFunc?.(value)

  return requiredValidateError || customValidateError || undefined
}

const requiredValidator = (value) => {
  const err = { type: 'REQUIRED_ERROR', highlight: false }
  if (value) {
    if (Array.isArray(value)) {
      return value.length === 0 ? err : undefined
    }
    const parsedValue = (typeof value === 'string') ? value.trim() : value
    return (parsedValue ? undefined : err)
  } else {
    return err
  }
}

const getColumnType = (colInfo) => {
  let type = 'text'
  if (colInfo.values && colInfo.valuesMap) {
    type = 'selectWithBadges'
  }

  if (colInfo.type === 'boolean') {
    type = 'switch'
  } else if (colInfo.type === 'array') {
    type = 'creatable_array'
  }
  return type
}

const getEditableArrayColumns = (origPropObject) => {
  return origPropObject.columnsOrder.map(colName => {
    const colInfo = origPropObject.columns[colName]
    const result = {
      name: colName,
      label: colInfo.title,
      required: colInfo.isRequired,
      type: getColumnType(colInfo),
    }
    if (colInfo.values) {
      result.options = colInfo.values.map(v => ({
        value: v,
        label: colInfo.valuesMap[v],
      }))
    }
    if (colInfo.maxLength) {
      result.maxLength = colInfo.maxLength
    }
    return result
  })
}

// convert property description to fieldParams format
// onTriggerPropChange - callback for onChange trigger props
const getFieldParamsForProp = (propName, prop, indexInGroup, onTriggerPropChange) => {
  const type = 'text'
  const fieldParamsForProp = {
    name: propName,
    fullName: propName,
    label: prop.title,
    type: type,
    ...prop.description && {
      tooltip: prop.description,
    },
    // if original prop can be null and field is required, it will be invalid in form
    // so we make such fields non-required
    required: Boolean(prop.isRequired && !prop.canBeNull),
    indexInGroup: indexInGroup,
  }

  // allOf and oneOf selects
  if (prop.values && prop.valuesMap) {
    fieldParamsForProp.type = 'select'
    fieldParamsForProp.options = prop.values.map(v => ({
      value: v,
      label: prop.valuesMap[v],
    }))

    if (fieldParamsForProp.options.length === 0) {
      fieldParamsForProp.disabled = true
    }
  }

  // for trigger props add onChange callback
  if (prop.propsByValues && onTriggerPropChange) {
    fieldParamsForProp.onChange = onTriggerPropChange
  }

  if (prop.format === 'password') {
    fieldParamsForProp.type = 'secure'
    fieldParamsForProp.multiline = Boolean(prop.multiline)

    if (fieldParamsForProp.multiline) {
      fieldParamsForProp.minRowsTextArea = prop.minRowsTextArea || prop.minRows || 5
    }
  } else if (prop.format === 'text') {
    fieldParamsForProp.type = 'textarea'
    fieldParamsForProp.minRowsTextArea = 5
  } else if (prop.format === 'datetime') {
    fieldParamsForProp.type = 'datetime'
  }

  if (prop.maxLength) {
    fieldParamsForProp.maxLength = prop.maxLength
  }

  if (prop.type === 'boolean') {
    fieldParamsForProp.type = 'switch'
  } else if (prop.type === 'color') {
    fieldParamsForProp.type = 'colorpicker'
  } else if (prop.type === 'image') {
    fieldParamsForProp.type = 'image'
  } else if (prop.type === 'integer') {
    fieldParamsForProp.type = 'integer'
    fieldParamsForProp.minNumber = prop.minNumber || 0
    fieldParamsForProp.defaultValue = prop.defaultValue || 0
  } else if (prop.type === 'readOnlyField' && prop.format === 'datetime') {
    fieldParamsForProp.type = 'datetime'
    fieldParamsForProp.shouldUseValue = true
    fieldParamsForProp.disabled = true
  } else if (prop.type === 'readOnlyField') {
    fieldParamsForProp.type = 'readOnlyField'
    fieldParamsForProp.shouldUseValue = true
  } else if (prop.type === 'array') {
    if (prop.columns && prop.columnsOrder) {
      fieldParamsForProp.type = 'editableArray'
      fieldParamsForProp.columns = getEditableArrayColumns(prop)
    } else if (prop.values && prop.valuesMap) { // multiselect
      fieldParamsForProp.type = 'select'
      fieldParamsForProp.options = prop.values.map(v => ({
        value: v,
        label: prop.valuesMap[v],
        bgColor: prop?.colorsMap ? prop.colorsMap[v] : bgSecondaryDefaultExpand,
      }))
      fieldParamsForProp.isMultiselect = true
    } else {
      fieldParamsForProp.type = 'creatable_array'
      fieldParamsForProp.allowCsvUpload = false
    }
  }

  return fieldParamsForProp
}

// Remove all top-level params from initialValues that were received from
// the backend but were not used in form like 'created_at', 'id'.
// Backend doesn't expect these parameters on submit and if we don't filter
// them here, we would need to do it in onChange()
const filterInitialValues = (initialValues, tabs, fieldGroups) => {
  const keepParams = []
  // collect params from tabs
  keepParams.push(...tabs.map(tab => tab.id))
  // collect params from general tab top level props and groups
  fieldGroups.forEach(fieldGroup => {
    if (!isGeneralTab(fieldGroup.tabId)) {
      return
    }

    if (fieldGroup.group === '') {
      fieldGroup.fieldParams.forEach(param => {
        keepParams.push(param.name)
      })
    } else {
      keepParams.push(fieldGroup.group)
    }
  })

  const filteredInitialValues = {}
  keepParams.forEach(paramName => {
    if (typeof initialValues[paramName] !== 'undefined') {
      filteredInitialValues[paramName] = initialValues[paramName]
    }
  })
  return filteredInitialValues
}

const isGeneralTab = (tabId) => {
  return tabId === 'general'
}

// Check if the prop is a trigger prop with dependent props and/or groups in propsByValues.
// It means that when its value changes, dependent props will be added below
// it to the same fieldParams group.
// If trigger prop has dependent groups they will be added to the fieldParams array
// below the trigger prop field group.
//
// flat array of fieldParams groups:
//   - top-level fieldParams group for tab
//     - *trigger prop*
//     - dependent prop1   | dynamic values
//     - dependent prop2   |
//   - dependent group1    |
//   - dependent group2    |
//
// Another case: trigger prop is inside the group. Only dependent props may exist (not gruops):
// flat array of fieldParams groups:
//   - top-level fieldParams group for tab
//   - group containing trigger prop
//     - *trigger prop*
//     - dependent prop1   | dynamic values
//     - dependent prop2   |
//     - some prop
//
const isInDependency = (prop) => {
  return !!prop?.propsByValues
}

// Return field params always in groupped mode with hidden groups if they are not needed)
//
// jsonSchemaEx - object with json schema extended with additional properties
//   {
//     tabsOrder: ['general', 'tab1', ...],
//     tabs: {
//       general: {
//         title: 'General',
//         propsOrder: ['prop1', 'prop2', ...],
//         props: {
//           prop1: {
//             title: 'Prop1',
//             type: 'string',
//             isRequired: true,
//             values: ['val1', 'val2'],
//             valuesMap: {
//               val1: 'Value 1',
//               val2: 'Value 2',
//             },
//             ...
//           },
//           ...
//         },
//         groupsOrder: ['group1', 'group2', ...],
//         groups: {
//           group1: {
//             key: 'group1',
//             title: 'Group 1',
//             propsOrder: ['prop3', 'prop4', ...],
//             props: {...},
//           },
//           ...
//         },
//       },
//       ...
//     },
//   }
// iniitialValues - object with initial values for form fields used for
//                  dynamic allOf sections and filtering of unused fields
// onTriggerPropChange - callback for onChange for trigger props
//
// Result: {
//   fieldParams: [{
//     group: '',       // group name or '' for hidden general tab,
//     groupTitle: '',  // group title or empty string if no groups,
//     tabId: '',       // optional tab name (for tabs),
//     groupPrefix: '', // optional group parameter prefix (for tab groups)
//                      // or '' for general tab,
//     fieldParams: [   // array of field params
//       {...},
//       ...
//     ],
//   }, ... ],
//   tabs: [{ id: 'tabId', title: 'tab title' }, ...],
//   formInitialValues: {...},  // initialValues with some parameters filtered
// }
export function getFieldParamsByJsonSchemaEx (jsonSchemaEx, initialValues, onTriggerPropChange) {
  const fieldGroups = []
  const tabs = []
  jsonSchemaEx.tabsOrder.forEach(tabId => {
    const tab = jsonSchemaEx.tabs[tabId]
    tabs.push({ id: tabId, title: tab.title })

    const { props, propsOrder, groups, groupsOrder } = tab
    const localInitialValues = isGeneralTab(tabId) ? initialValues : initialValues[tabId]

    // recursively get field groups for top level props of current tab, for groups
    // and for all possible dependencies of trigger props
    const { fieldGroups: localFieldGroups } = processPropsAndGroups({
      props: props,
      propsOrder: propsOrder,
      groups: groups,
      groupsOrder: groupsOrder,
      tabId: tabId,
      initialValues: localInitialValues,
      group: null,
      triggerProp: null,
      onTriggerPropChange: onTriggerPropChange,
    })

    fieldGroups.push(...localFieldGroups)
  })

  const filteredInitialValues = filterInitialValues(initialValues, tabs, fieldGroups)
  return ({
    fieldParams: fieldGroups,
    tabs: tabs,
    filteredInitialValues: filteredInitialValues,
  })
}

// Recursively get field groups for top-level props, for groups
// and for dependencies of trigger props.
// triggerProp is set when processing dependencies for this prop
const processPropsAndGroups = ({
  props, propsOrder, groups, groupsOrder, tabId, initialValues,
  group, triggerProp, onTriggerPropChange,
}) => {
  // Recursively process top-level props and their possible dependencies
  // and get field groups.
  // For dependencies get fieldParams of dependent top level props
  // and return them to add to existing group in outer function
  const { fieldParams, fieldGroups: fieldGroupsForProps } = processProps({
    props: props,
    propsOrder: propsOrder,
    tabId: tabId,
    initialValues: initialValues,
    group: group,
    triggerProp: triggerProp,
    onTriggerPropChange: onTriggerPropChange,
  })

  // get fieldGroups for properties in groups
  const fieldGroupsForGroups = []

  // get field groups for nested groups
  groupsOrder.forEach(groupName => {
    // we don't have nested 2nd level groups so we can ignore and overwrite
    // passed group parameter, it is null/undefined
    const group = groups[groupName]
    const groupInitialValues = initialValues[groupName]
    const { fieldGroups: fieldGroupsForGroup } = processProps({
      props: group.props,
      propsOrder: group.propsOrder,
      tabId: tabId,
      initialValues: groupInitialValues,
      group: group,
      triggerProp: triggerProp,
      onTriggerPropChange: onTriggerPropChange,
    })
    fieldGroupsForGroups.push(...fieldGroupsForGroup)
  })

  // combine all field groups and return
  const localFieldGroups = [...fieldGroupsForProps, ...fieldGroupsForGroups]

  return { fieldParams: fieldParams, fieldGroups: localFieldGroups }
}

// Process specified props of tab or group, recursively checking dependencies
// for top-level props.
// triggerProp - if exists, we are processing dependent props or props of dependent group
// group - if exists, props are in this group (but dependency breaks group relation
//         e.g. dependent props don't have group even if trigger prop was in some group)
//
// It accumulates data from deeper levels of recursive calls and returns
// optional fieldParams and optional fieldGroups creating groups directly or
// in the recursive calls (for dependent groups).
// fieldParams is used to append dependent props without creating a group
const processProps = ({
  props, propsOrder, tabId, initialValues, group, triggerProp, onTriggerPropChange,
}) => {
  const localFieldParams = []
  const allDependentFieldGroups = []
  const isInDependency_ = isInDependency(triggerProp)

  // recursively process all props and their possible dependencies
  // it may call processProps() recursively:
  //   processProp => processPropDependencies => processPropsAndGroups => processProps
  propsOrder.forEach((propName, indexInGroup) => {
    const prop = props[propName]
    const {
      fieldParamsObjectForProp, dependentFieldParams, dependentFieldGroups,
    } = processProp({
      propName: propName,
      prop: prop,
      indexInGroup: indexInGroup,
      initialValues: initialValues,
      tabId: tabId,
      group: group,
      triggerProp: triggerProp,
      onTriggerPropChange: onTriggerPropChange,
    })
    localFieldParams.push(fieldParamsObjectForProp)

    // if current prop is a trigger prop with dependent props,
    // append them as dependent field params to process at upper levels
    if (dependentFieldParams?.length > 0) {
      localFieldParams.push(...dependentFieldParams)
      // remember dependent props so we can reset them later in the onChange
      // event handler of the trigger prop
      fieldParamsObjectForProp.dependentFieldParams = dependentFieldParams
    }

    // add other dependent field groups (if any)
    if (dependentFieldGroups?.length > 0) {
      allDependentFieldGroups.push(...dependentFieldGroups)
      // remember dependent groups
      fieldParamsObjectForProp.dependentFieldGroups = dependentFieldGroups
    }
  })

  // return without creating a new field group for dependent
  // top-level dependent props (not for the dependent group props)
  // returns processed fieldParams and groups which will be
  // appended after the trigger prop in the same field group.
  if (isInDependency_ && !group) {
    return { fieldParams: localFieldParams, fieldGroups: allDependentFieldGroups }
  }

  // reassign the indexInGroup to ensure sequential numbering
  if (!isInDependency_) {
    localFieldParams.forEach((param, indexInGroup) => { param.indexInGroup = indexInGroup })
  }

  // create a field group for the passed props e.g.
  //  - tab top-level props OR
  //  - props of groups OR
  //  - props of dependent groups
  const fieldGroup = createFieldGroup({
    fieldParams: localFieldParams,
    tabId: tabId,
    group: group,
    triggerProp: triggerProp,
  })
  const fieldGroups = [fieldGroup, ...allDependentFieldGroups]
  return { fieldParams: [], fieldGroups: fieldGroups }
}

// Converts current prop params to field params map
// and if the prop has dependencies, process them too.
//
// Returns:
//   fieldParamsObjectForProp - field params map for the current prop
//   dependentFieldParams     - field params array for optional dependent props
//   dependentFieldGroups     - other optional dependent field groups, already processed by nested calls
const processProp = ({
  propName, prop, indexInGroup, tabId, initialValues,
  group, triggerProp, onTriggerPropChange,
}) => {
  const fieldParamsObjectForProp = getFieldParamsForProp(propName, prop, indexInGroup, onTriggerPropChange)
  const isInDependency_ = isInDependency(prop)

  // do not try to add dependencies if we are already inside of the processing
  // of the dependent props or if the current prop is not a trigger prop
  if (triggerProp || !isInDependency_) {
    return {
      fieldParamsObjectForProp: fieldParamsObjectForProp,
      dependentFieldParams: [],
      dependentFieldGroups: [],
    }
  }

  // get dependent props and groups for the current prop (trigger prop)
  const { dependentFieldParams, dependentFieldGroups } = processPropDependencies({
    triggerProp: prop,
    triggerPropValue: initialValues[propName],
    initialValues: initialValues,
    tabId: tabId,
  })

  return {
    fieldParamsObjectForProp: fieldParamsObjectForProp,
    dependentFieldParams: dependentFieldParams,
    dependentFieldGroups: dependentFieldGroups,
  }
}

// If prop (potential trigger prop) has dependent
// props and groups, process them
//
// Returns object with keys:
//   dependentFieldParams - optional non-grouped field params of dependent props
//   dependentFieldGroups - field groups of the dependency
const processPropDependencies = ({
  triggerProp, triggerPropValue,
  initialValues, tabId, group,
}) => {
  const depPropsAndGroups = triggerProp.propsByValues[triggerPropValue]

  // just in case, check that there are dependent props and groups
  if (!depPropsAndGroups) {
    return {
      dependentFieldParams: [],
      dependentFieldGroups: [],
    }
  }

  const { props, propsOrder, groups, groupsOrder } = depPropsAndGroups

  // getting processed dependent props and groups
  const {
    fieldParams: depFieldParams,
    fieldGroups: depFieldGroups,
  } = processPropsAndGroups({
    props: props,
    propsOrder: propsOrder,
    groups: groups,
    groupsOrder: groupsOrder,
    initialValues: initialValues,
    tabId: tabId,
    group: group,
    triggerProp: triggerProp,
  })

  return {
    dependentFieldParams: depFieldParams,
    dependentFieldGroups: depFieldGroups,
  }
}

// Create field group in form builder format which contains
// group description and fields' descriptions in fieldParams array.
// groupPrefix and group name are optional and used to generate fields' names:
//   `${groupPrefix}.${group}.${propName}`
// If groupTitle is absent, group will be titlized
const createFieldGroup = ({
  fieldParams, tabId, group, triggerProp,
}) => {
  const isGeneralTab_ = isGeneralTab(tabId)
  const isTopLevelPropsContainer = !group

  let group_
  if (isTopLevelPropsContainer) {
    group_ = isGeneralTab_ ? '' : tabId
  } else {
    group_ = group.key
  }

  let groupTitle
  if (isTopLevelPropsContainer) {
    groupTitle = ''
  } else {
    groupTitle = group.title
  }

  let groupPrefix
  if (isTopLevelPropsContainer) {
    groupPrefix = undefined
  } else {
    groupPrefix = isGeneralTab_ ? undefined : tabId
  }

  // now when we know the group we can add full form name for fields in group
  // it simplifies updating dependent fields on trigger prop change
  fieldParams.forEach(field => {
    const fullName =
      (groupPrefix ? `${groupPrefix}.` : '') +
      (group_ ? `${group_}.` : '') +
      field.name
    field.fullName = fullName
  })

  // make resulting params cleaner removing undefined values
  // it also simplifies unit test examples generation as json convertions delete undefined values
  const fieldGroup = removeUndefinedValues({
    group: group_,
    groupTitle: groupTitle,
    groupPrefix: groupPrefix,
    tabId: tabId,
    fieldParams: fieldParams,
  })

  return fieldGroup
}
